import { useState, useMemo, useEffect, useCallback, useRef, type MouseEvent } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { FiList, FiFlag } from 'react-icons/fi';
import { FaFileImport } from 'react-icons/fa';
import { TbArchive, TbHistory, TbTransferIn } from 'react-icons/tb';
import {
    useInventoryFeed,
    type InventoryItemEntry,
} from '../../../hooks/inventory/useInventoryFeed';
import { useInventorySummary } from '../../../hooks/inventory/useInventorySummary';
import {
    useBulkUpdateInventoryStatus,
    useUpdateInventoryItem,
} from '../../../hooks/inventory/useUpdateInventoryItem';
import {
    useBulkDeleteInventoryItems,
} from '../../../hooks/inventory/useDeleteInventoryItem';
import { useCreateInventoryImportJob, useInventoryImportJobsList } from '../../../hooks/inventory/useInventoryImportJobs';
import {
    InventoryAdditionalFilters,
    InventoryStatus,
    InventoryStatusFilter,
} from '../../../types/inventory';
import {
    INVENTORY_IMPORT_TARGET_FIELDS,
    mappedRowsToImportItems,
} from '../../../functions/import/inventoryImportFields';
import { parseInventoryPartType } from '../../../utils/inventoryConstants';
import { useGetLocations } from '../../../hooks/useGetLocations';
import { useRecentActivity } from '../../../hooks/useRecentActivity';
import { useTableSettings } from '../../../hooks/useTableSettings';
import { useColumnWidths } from '../../../hooks/useColumnWidths';
import { useScrollShadow } from '../../../hooks/useScrollShadow';
import { useResponsiveToolbar } from '../../../hooks/useResponsiveToolbar';
import {
    handleOrderBulkRowClick,
    useOrderBulkSelection,
} from '../../../hooks/useOrderBulkSelection';
import type { InventoryBulkActionId } from '../../../components/inventory/InventoryBulkActionBar';
import { INVENTORY_LIST_RESET_STATE_KEY } from '../../../shared/inventoryListViewState';
import { recallListSearch } from '../../../shared/listSearchMemory';
import { useRememberListSearch } from '../../../hooks/useRememberListSearch';

export const INVENTORY_LIST_TAB_ICONS: Record<string, React.ElementType> = {
    active: FiList as React.ElementType,
    flagged: FiFlag as React.ElementType,
    discontinued: TbArchive as React.ElementType,
    importPreview: FaFileImport as React.ElementType,
    purchaseOrders: TbTransferIn as React.ElementType,
};

export const INVENTORY_ACTIVITY_BUTTON_ICON: React.ElementType = TbHistory as React.ElementType;

const VALID_STATUS_FILTERS: InventoryStatusFilter[] = ['active', 'flagged', 'discontinued'];

const INVENTORY_LOCATION_STORAGE_KEY = 'inventory_location_ids';

type InventoryListViewMode = 'list' | 'importPreview' | 'purchaseOrders';

/**
 * Defaults to 'all' (server clamps to the session's authorized locations)
 * rather than []. Single-location users never see the LocationSelector, so an
 * empty default could never be corrected and would scope feeds to nothing.
 */
function loadInventoryLocationIds(): Array<string> | 'all' {
    if (typeof window === 'undefined') return 'all';
    try {
        const stored = localStorage.getItem(INVENTORY_LOCATION_STORAGE_KEY);
        if (stored) {
            const parsed = stored === 'all' ? 'all' : JSON.parse(stored);
            if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
                return parsed;
            }
        }
    } catch {
        // ignore
    }
    return 'all';
}

export function useInventoryListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const queryClient = useQueryClient();
    const { trackView } = useRecentActivity();
    const { settings: tableSettings, updateSettings: updateTableSettings, tableSettingsSx } =
        useTableSettings('inventory');
    const { widths: colWidths, updateWidth: updateColWidth, resetWidths: resetColWidths } =
        useColumnWidths('inventory');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { showRightShadow } = useScrollShadow(scrollContainerRef);
    const { containerRef: toolbarRef, locationCompact, searchCompact } = useResponsiveToolbar();

    const isPurchaseOrdersRoute = location.pathname.startsWith('/inventory/purchase-orders');
    const statusFilterFromUrl = (VALID_STATUS_FILTERS.includes(
        searchParams.get('statusFilter') as InventoryStatusFilter,
    )
        ? searchParams.get('statusFilter')
        : 'active') as InventoryStatusFilter;
    const viewFromUrl = searchParams.get('view');
    const importJobIdFromUrl = searchParams.get('importJobId');
    const purchaseOrderIdFromUrl = searchParams.get('purchaseOrderId');

    const initialViewMode: InventoryListViewMode = isPurchaseOrdersRoute
        ? 'purchaseOrders'
        : viewFromUrl === 'importPreview'
            ? 'importPreview'
            : 'list';

    const [statusFilter, setStatusFilter] = useState<InventoryStatusFilter>(statusFilterFromUrl);
    const [listViewMode, setListViewMode] = useState<InventoryListViewMode>(initialViewMode);
    const [selectedImportJobId, setSelectedImportJobId] = useState<string | null>(
        importJobIdFromUrl,
    );
    const [focusedPurchaseOrderId, setFocusedPurchaseOrderId] = useState<string | null>(
        purchaseOrderIdFromUrl,
    );
    const [purchaseOrdersSearchQuery, setPurchaseOrdersSearchQuery] = useState('');
    const [importFile, setImportFile] = useState<File | null>(null);
    const [optimisticImports, setOptimisticImports] = useState<
        Array<{
            tempId: string;
            status: 'uploading' | 'failed';
            totalItemCount: number;
            createdAt: string;
            errorMessage?: string;
        }>
    >([]);
    const [selectedOptimisticTempId, setSelectedOptimisticTempId] = useState<string | null>(null);
    const {
        isOpen: isImportUploadOpen,
        onOpen: onImportUploadOpen,
        onClose: onImportUploadClose,
    } = useDisclosure();
    const {
        isOpen: isImportMappingOpen,
        onOpen: onImportMappingOpen,
        onClose: onImportMappingClose,
    } = useDisclosure();
    const { data: importJobsData, isFetched: importJobsFetched } = useInventoryImportJobsList(true);
    const hasPendingImportJob =
        optimisticImports.some((j) => j.status === 'uploading' || j.status === 'failed') ||
        (importJobsData?.jobs ?? []).some((j) => j.isPending && j.status !== 'committed');

    const [locationIds, setLocationIds] = useState<Array<string> | 'all'>(loadInventoryLocationIds);
    // Search is never written to the URL or storage, so a refresh or sidebar
    // click starts empty; browser back/forward to this same history entry
    // brings it back from the page-load memory.
    const [searchQuery, setSearchQuery] = useState(() => recallListSearch(location.key) ?? '');
    const [debouncedSearch, setDebouncedSearch] = useState(() => recallListSearch(location.key) ?? '');
    const lastAppliedSearch = useRef(debouncedSearch.trim());
    // A search restored by history is not a typed edit: set both values and mark
    // it applied so the debounce below does not reset the page.
    const restoreSearch = useCallback((search: string) => {
        setSearchQuery(search);
        setDebouncedSearch(search);
        lastAppliedSearch.current = search.trim();
    }, []);
    useRememberListSearch(searchQuery, restoreSearch);
    const [currentPage, setCurrentPage] = useState<number>(() => {
        if (typeof window === 'undefined') return 1;
        try {
            const saved = localStorage.getItem('inventory_list_page');
            if (saved) {
                const n = parseInt(saved, 10);
                if (!Number.isNaN(n) && n > 0) return n;
            }
        } catch {
            // ignore
        }
        return 1;
    });
    const [additionalFilters, setAdditionalFilters] = useState<InventoryAdditionalFilters>(() => {
        try {
            const saved = localStorage.getItem('inventory_advanced_filters');
            if (!saved) return {};
            const parsed = JSON.parse(saved);
            if (parsed.dateAddedRange) {
                parsed.dateAddedRange = {
                    start: new Date(parsed.dateAddedRange.start),
                    end: new Date(parsed.dateAddedRange.end),
                };
            }
            // Migrate legacy single-value category/partType to arrays
            if (parsed.category != null && !Array.isArray(parsed.category)) {
                parsed.category = [parsed.category];
            }
            if (parsed.partType != null && !Array.isArray(parsed.partType)) {
                parsed.partType = [parsed.partType];
            }
            if (Array.isArray(parsed.partType)) {
                parsed.partType = parsed.partType.map((v: string) =>
                    parseInventoryPartType(v),
                );
            }
            return parsed;
        } catch {
            return {};
        }
    });

    const [selectedItemId, setSelectedItemId] = useState<string | null>(() =>
        searchParams.get('itemId'),
    );
    const [deleteTargetItem, setDeleteTargetItem] = useState<InventoryItemEntry | null>(null);

    const { isOpen: isAddDrawerOpen, onOpen: onAddDrawerOpen, onClose: onAddDrawerClose } =
        useDisclosure();
    const {
        isOpen: isEditDrawerOpen,
        onOpen: onEditDrawerOpen,
        onClose: onEditDrawerClose,
    } = useDisclosure();
    const {
        isOpen: isDeleteModalOpen,
        onOpen: onDeleteModalOpen,
        onClose: onDeleteModalClose,
    } = useDisclosure();
    const {
        isOpen: isActivityLogOpen,
        onOpen: onActivityLogOpen,
        onClose: onActivityLogClose,
    } = useDisclosure();
    const {
        isOpen: isBulkDeleteOpen,
        onOpen: onBulkDeleteOpen,
        onClose: onBulkDeleteClose,
    } = useDisclosure();
    const {
        isOpen: isImportHistoryOpen,
        onOpen: onImportHistoryOpen,
        onClose: onImportHistoryClose,
    } = useDisclosure();
    const {
        isOpen: isExportHistoryOpen,
        onOpen: onExportHistoryOpen,
        onClose: onExportHistoryClose,
    } = useDisclosure();
    const {
        isOpen: isOptionsOpen,
        onOpen: onOptionsOpen,
        onClose: onOptionsClose,
    } = useDisclosure();
    const [historyOpenedFromOptions, setHistoryOpenedFromOptions] = useState(false);
    const [isOptionsSwapInstant, setIsOptionsSwapInstant] = useState(false);
    const [highlightExportId, setHighlightExportId] = useState<string | null>(null);
    const [committedModalJobId, setCommittedModalJobId] = useState<string | null>(null);

    const { data: locationsData } = useGetLocations(true);

    const limit = 20;
    const queryLimit = limit + 1;

    const statusFilterParam = searchParams.get('statusFilter');
    const itemIdParam = searchParams.get('itemId');
    const viewParam = searchParams.get('view');
    const importJobIdParam = searchParams.get('importJobId');
    const purchaseOrderIdParam = searchParams.get('purchaseOrderId');

    useEffect(() => {
        const filterFromUrl = (VALID_STATUS_FILTERS.includes(
            statusFilterParam as InventoryStatusFilter,
        )
            ? statusFilterParam
            : 'active') as InventoryStatusFilter;

        if (filterFromUrl !== statusFilter) setStatusFilter(filterFromUrl);
        if (itemIdParam && itemIdParam !== selectedItemId) {
            setSelectedItemId(itemIdParam);
            onEditDrawerOpen();
        }
        const nextView: InventoryListViewMode = isPurchaseOrdersRoute
            ? 'purchaseOrders'
            : viewParam === 'importPreview'
                ? 'importPreview'
                : 'list';
        if (nextView !== listViewMode) setListViewMode(nextView);
        if ((importJobIdParam || null) !== selectedImportJobId) {
            setSelectedImportJobId(importJobIdParam);
        }
        if ((purchaseOrderIdParam || null) !== focusedPurchaseOrderId) {
            setFocusedPurchaseOrderId(purchaseOrderIdParam);
        }
        // URL params are the source of truth for this sync; local state is only
        // written when it differs from the URL-derived values above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilterParam, itemIdParam, viewParam, importJobIdParam, purchaseOrderIdParam, isPurchaseOrdersRoute]);

    useEffect(() => {
        if (!importJobsFetched || hasPendingImportJob) return;
        if (listViewMode !== 'importPreview' && viewParam !== 'importPreview') return;
        setListViewMode('list');
        setSelectedImportJobId(null);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('view');
        newSearchParams.delete('importJobId');
        setSearchParams(newSearchParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPendingImportJob, importJobsFetched]);

    useEffect(() => {
        const state = location.state as { [INVENTORY_LIST_RESET_STATE_KEY]?: boolean } | null;
        if (!state?.[INVENTORY_LIST_RESET_STATE_KEY]) return;
        // The sidebar asked for a fresh list (it may already be mounted): drop the
        // search and go back to page 1, then consume the flag so a refresh is inert.
        setSearchQuery('');
        setDebouncedSearch('');
        setCurrentPage(1);
        const { [INVENTORY_LIST_RESET_STATE_KEY]: _consumed, ...rest } = state;
        navigate(
            { pathname: location.pathname, search: location.search },
            { replace: true, state: rest },
        );
    }, [location.pathname, location.search, location.state, navigate]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            if (isPurchaseOrdersRoute) return;
            if (lastAppliedSearch.current !== searchQuery.trim()) {
                lastAppliedSearch.current = searchQuery.trim();
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery, isPurchaseOrdersRoute]);

    useEffect(() => {
        localStorage.setItem('inventory_advanced_filters', JSON.stringify(additionalFilters));
    }, [additionalFilters]);

    useEffect(() => {
        try {
            localStorage.setItem('inventory_list_page', String(currentPage));
        } catch {
            // ignore
        }
    }, [currentPage]);

    useEffect(() => {
        const label = `Inventory • ${statusFilter[0].toUpperCase() + statusFilter.slice(1)}`;
        trackView({
            type: 'inventory_tab',
            key: `inventory_tab:${statusFilter}`,
            label,
            path: '/inventory',
            meta: { tab: statusFilter },
        });
    }, [trackView, statusFilter]);

    const {
        data: inventoryData,
        isLoading,
        isRefetching,
        error,
        refetch,
    } = useInventoryFeed(
        statusFilter,
        locationIds,
        currentPage,
        queryLimit,
        debouncedSearch,
        additionalFilters,
    );

    const showListChrome =
        listViewMode !== 'importPreview' && listViewMode !== 'purchaseOrders';

    const {
        data: inventorySummary,
        isLoading: isSummaryLoading,
        isFetching: isSummaryFetching,
        refetch: refetchSummary,
    } = useInventorySummary(
        statusFilter,
        locationIds,
        debouncedSearch,
        additionalFilters,
        showListChrome,
    );

    const { pageItems, hasNextPage } = useMemo(() => {
        const fetched = inventoryData?.items || [];
        return {
            pageItems: fetched.slice(0, limit),
            hasNextPage: fetched.length > limit,
        };
    }, [inventoryData?.items, limit]);

    const pageItemIds = useMemo(() => pageItems.map((item) => item._id), [pageItems]);
    const {
        selectedOrderIds: selectedItemIds,
        toggleOrderSelected: toggleItemSelected,
        toggleSelectAllOnPage,
        allPageSelected,
        isPageSelectionIndeterminate,
        clearSelection,
    } = useOrderBulkSelection({ orderIds: pageItemIds });

    useEffect(() => {
        clearSelection();
    }, [statusFilter, currentPage, debouncedSearch, locationIds, additionalFilters, clearSelection]);

    const selectedStatuses = useMemo(() => {
        const byId = new Map(pageItems.map((item) => [item._id, item.status]));
        const statuses: InventoryStatus[] = [];
        selectedItemIds.forEach((id) => {
            const status = byId.get(id);
            if (status) statuses.push(status);
        });
        return statuses;
    }, [pageItems, selectedItemIds]);

    const invalidateInventoryQueries = useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['/inventory/feed'] }),
            queryClient.invalidateQueries({ queryKey: ['/inventory/summary'] }),
            queryClient.invalidateQueries({ queryKey: ['/inventory/activity-logs'] }),
        ]);
    }, [queryClient]);

    const updateStatusMutation = useUpdateInventoryItem({
        onSuccess: async () => {
            await invalidateInventoryQueries();
            toast({
                title: 'Status updated',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        },
        onError: (error: unknown) => {
            const message = (error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message;
            toast({
                title: 'Failed to update status',
                description: message ?? 'Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const bulkStatusMutation = useBulkUpdateInventoryStatus({
        onSuccess: async (result) => {
            await invalidateInventoryQueries();
            clearSelection();
            const missing = Math.max(result.requestedCount - result.updatedCount, 0);
            toast({
                title:
                    missing > 0
                        ? `${result.updatedCount} of ${result.requestedCount} items updated`
                        : `${result.updatedCount} item${result.updatedCount === 1 ? '' : 's'} updated`,
                description:
                    missing > 0
                        ? `${missing} item${missing === 1 ? '' : 's'} could not be updated.`
                        : undefined,
                status: missing > 0 ? 'warning' : 'success',
                duration: 4000,
                isClosable: true,
            });
        },
        onError: (error: unknown) => {
            const message = (error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message;
            toast({
                title: 'Failed to update items',
                description: message ?? 'Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const bulkDeleteMutation = useBulkDeleteInventoryItems({
        onSuccess: async (result) => {
            await invalidateInventoryQueries();
            clearSelection();
            onBulkDeleteClose();
            const missing = Math.max(result.requestedCount - result.deletedCount, 0);
            toast({
                title:
                    missing > 0
                        ? `${result.deletedCount} of ${result.requestedCount} items deleted`
                        : `${result.deletedCount} item${result.deletedCount === 1 ? '' : 's'} deleted`,
                description:
                    missing > 0
                        ? `${missing} item${missing === 1 ? '' : 's'} could not be deleted.`
                        : undefined,
                status: missing > 0 ? 'warning' : 'success',
                duration: 4000,
                isClosable: true,
            });
        },
        onError: (error: unknown) => {
            const message = (error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message;
            toast({
                title: 'Failed to delete items',
                description: message ?? 'Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const totalPages = useMemo(() => {
        const totalItems = inventoryData?.totalItems ?? 0;
        const computedTotalPages = Math.ceil(totalItems / limit);
        return Math.max(1, computedTotalPages || currentPage);
    }, [inventoryData?.totalItems, limit, currentPage]);

    const openItemDrawer = useCallback(
        (itemId: string) => {
            setSelectedItemId(itemId);
            onEditDrawerOpen();
        },
        [onEditDrawerOpen],
    );

    const handleRowClick = (
        item: InventoryItemEntry,
        event?: MouseEvent,
        rowIndex?: number,
    ) => {
        if (event && rowIndex != null) {
            handleOrderBulkRowClick(
                event,
                item._id,
                rowIndex,
                toggleItemSelected,
                () => openItemDrawer(item._id),
                true,
            );
            return;
        }
        openItemDrawer(item._id);
    };

    const handleRowStatusChange = (item: InventoryItemEntry, status: InventoryStatus) => {
        if (item.status === status) return;
        updateStatusMutation.mutate({
            inventoryItemId: item._id,
            changes: { status },
        });
    };

    const handleBulkAction = (actionId: InventoryBulkActionId) => {
        const ids = Array.from(selectedItemIds);
        if (ids.length === 0) return;
        if (actionId === 'delete') {
            onBulkDeleteOpen();
            return;
        }
        bulkStatusMutation.mutate({
            inventoryItemIds: ids,
            changes: { status: actionId === 'activate' ? 'active' : 'discontinued' },
        });
    };

    const handleBulkDeleteConfirm = (confirmPhrase: string) => {
        const ids = Array.from(selectedItemIds);
        if (ids.length === 0) return;
        bulkDeleteMutation.mutate({
            inventoryItemIds: ids,
            confirmPhrase,
        });
    };

    const openEditDrawer = (inventoryItemId: string) => {
        openItemDrawer(inventoryItemId);
    };

    const handleEditDrawerClose = () => {
        onEditDrawerClose();
        setSelectedItemId(null);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('itemId');
        setSearchParams(newSearchParams, { replace: true });
    };

    const handleDeleteRequest = (item: InventoryItemEntry) => {
        setDeleteTargetItem(item);
        onDeleteModalOpen();
    };

    const handleDeleteModalClose = () => {
        onDeleteModalClose();
        setDeleteTargetItem(null);
    };

    const handleItemDeleted = () => {
        if (deleteTargetItem && deleteTargetItem._id === selectedItemId) {
            handleEditDrawerClose();
        }
    };

    const handleViewExistingItem = (inventoryItemId: string) => {
        onAddDrawerClose();
        openItemDrawer(inventoryItemId);
    };

    const handleRefresh = () => {
        refetch();
        refetchSummary();
        queryClient.invalidateQueries({ queryKey: ['/inventory/purchase-orders/feed'] });
        queryClient.invalidateQueries({ queryKey: ['/inventory/summary'] });
    };

    const handleStatusFilterChange = (newFilter: InventoryStatusFilter) => {
        setStatusFilter(newFilter);
        setListViewMode('list');
        setSearchQuery('');
        setDebouncedSearch('');
        setCurrentPage(1);
        setFocusedPurchaseOrderId(null);
        const newSearchParams = new URLSearchParams();
        if (newFilter !== 'active') {
            newSearchParams.set('statusFilter', newFilter);
        }
        if (isPurchaseOrdersRoute) {
            const qs = newSearchParams.toString();
            navigate(qs ? `/inventory?${qs}` : '/inventory', { replace: true });
            return;
        }
        setSearchParams(newSearchParams, { replace: true });
    };

    const handlePurchaseOrdersTab = () => {
        setListViewMode('purchaseOrders');
        const newSearchParams = new URLSearchParams();
        if (focusedPurchaseOrderId) {
            newSearchParams.set('purchaseOrderId', focusedPurchaseOrderId);
        }
        const qs = newSearchParams.toString();
        navigate(qs ? `/inventory/purchase-orders?${qs}` : '/inventory/purchase-orders', {
            replace: true,
        });
    };

    const handleImportPreviewTab = () => {
        setListViewMode('importPreview');
        if (isPurchaseOrdersRoute) {
            navigate('/inventory?view=importPreview', { replace: true });
            return;
        }
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('view', 'importPreview');
        newSearchParams.delete('purchaseOrderId');
        setSearchParams(newSearchParams, { replace: true });
    };

    const handleSelectImportJobId = useCallback(
        (jobId: string) => {
            setSelectedOptimisticTempId(null);
            setSelectedImportJobId(jobId);
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('view', 'importPreview');
            newSearchParams.set('importJobId', jobId);
            setSearchParams(newSearchParams, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const handleSelectOptimisticTempId = useCallback(
        (tempId: string | null) => {
            setSelectedOptimisticTempId(tempId);
            if (tempId) {
                setSelectedImportJobId(null);
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set('view', 'importPreview');
                newSearchParams.delete('importJobId');
                setSearchParams(newSearchParams, { replace: true });
            }
        },
        [searchParams, setSearchParams],
    );

    const createImportJobMutation = useCreateInventoryImportJob();

    const handleRequestImport = () => {
        onImportUploadOpen();
    };

    const handleImportFileSelected = (file: File) => {
        onImportUploadClose();
        setImportFile(file);
        onImportMappingOpen();
    };

    const handleImportUploadClose = () => {
        onImportUploadClose();
    };

    const handleImportMappingClose = () => {
        onImportMappingClose();
        setImportFile(null);
    };

    const handleImportMappingSubmit = async (mappedRows: Array<Record<string, string>>) => {
        const items = mappedRowsToImportItems(mappedRows);
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setOptimisticImports((prev) => [
            {
                tempId,
                status: 'uploading',
                totalItemCount: items.length,
                createdAt: new Date().toISOString(),
            },
            ...prev,
        ]);
        setSelectedOptimisticTempId(tempId);
        setSelectedImportJobId(null);
        setListViewMode('importPreview');
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('view', 'importPreview');
        newSearchParams.delete('importJobId');
        setSearchParams(newSearchParams, { replace: true });

        void (async () => {
            try {
                const { job } = await createImportJobMutation.mutateAsync({ items });
                // Keep optimistic UPLOADING card until jobs/list includes this job so
                // hasPendingImportJob never dips false (which exits Import Preview).
                setSelectedImportJobId(job.jobId);
                const sp = new URLSearchParams(window.location.search);
                sp.set('view', 'importPreview');
                sp.set('importJobId', job.jobId);
                setSearchParams(sp, { replace: true });
                await queryClient.invalidateQueries({
                    queryKey: ['/inventory/import/jobs/list'],
                });
                setOptimisticImports((prev) => prev.filter((j) => j.tempId !== tempId));
                setSelectedOptimisticTempId((cur) => (cur === tempId ? null : cur));
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Import upload failed';
                setOptimisticImports((prev) =>
                    prev.map((j) =>
                        j.tempId === tempId
                            ? { ...j, status: 'failed' as const, errorMessage: message }
                            : j,
                    ),
                );
                toast({
                    title: 'Import failed',
                    description: message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        })();

        return `/inventory?view=importPreview`;
    };

    useEffect(() => {
        if (!isOptionsSwapInstant) return;
        const raf = requestAnimationFrame(() => setIsOptionsSwapInstant(false));
        return () => cancelAnimationFrame(raf);
    }, [isOptionsSwapInstant]);

    // Inventory Options <-> history read as one panel changing pages. The closing
    // drawer must re-render instant before `isOpen` flips (Chakra freezes exit
    // motion), hence the flushSync ahead of the swap.
    const swapOptionsSubPage = useCallback((swap: () => void) => {
        flushSync(() => setIsOptionsSwapInstant(true));
        swap();
    }, []);

    const openExportHistory = useCallback(() => {
        swapOptionsSubPage(() => {
            onOptionsClose();
            setHighlightExportId(null);
            setHistoryOpenedFromOptions(true);
            onExportHistoryOpen();
        });
    }, [swapOptionsSubPage, onOptionsClose, onExportHistoryOpen]);

    const openImportHistory = useCallback(() => {
        swapOptionsSubPage(() => {
            onOptionsClose();
            setHistoryOpenedFromOptions(true);
            onImportHistoryOpen();
        });
    }, [swapOptionsSubPage, onOptionsClose, onImportHistoryOpen]);

    const handleHistoryBackToOptions = useCallback(() => {
        swapOptionsSubPage(() => {
            onImportHistoryClose();
            onExportHistoryClose();
            onOptionsOpen();
        });
    }, [swapOptionsSubPage, onImportHistoryClose, onExportHistoryClose, onOptionsOpen]);

    const handleImportActivityClick = useCallback((jobId: string) => {
        setCommittedModalJobId(jobId);
    }, []);

    const handleExportActivityClick = useCallback(
        (exportId: string) => {
            setHighlightExportId(exportId);
            setHistoryOpenedFromOptions(false);
            onExportHistoryOpen();
        },
        [onExportHistoryOpen],
    );

    const handleImportHistorySelectJob = useCallback((jobId: string) => {
        onImportHistoryClose();
        setCommittedModalJobId(jobId);
    }, [onImportHistoryClose]);

    const buildFilteredExportInput = useCallback(
        () => ({
            mode: 'filtered' as const,
            statusFilter,
            locationIds,
            search: debouncedSearch || undefined,
            additionalFilters:
                Object.keys(additionalFilters).length > 0 ? additionalFilters : undefined,
        }),
        [statusFilter, locationIds, debouncedSearch, additionalFilters],
    );

    const handleLocationChange = useCallback(
        (newLocationIds: Array<string> | 'all') => {
            setLocationIds(newLocationIds);
            setCurrentPage(1);
            setAdditionalFilters((prev) => {
                if (!prev.binIds?.length) return prev;
                const next = { ...prev };
                delete next.binIds;
                return next;
            });
        },
        [],
    );

    const handleFiltersChange = useCallback((newFilters: InventoryAdditionalFilters) => {
        setAdditionalFilters(newFilters);
        setCurrentPage(1);
    }, []);

    return {
        inventoryData,
        statusFilter,
        searchQuery,
        setSearchQuery,
        purchaseOrdersSearchQuery,
        setPurchaseOrdersSearchQuery,
        locationIds,
        additionalFilters,
        currentPage,
        setCurrentPage,
        pageItems,
        hasNextPage,
        totalPages,
        isLoading,
        isRefetching,
        error,
        totalInventoryValue: inventorySummary?.inStockValue,
        isSummaryLoading: isSummaryLoading || (isSummaryFetching && !inventorySummary),
        tableSettings,
        updateTableSettings,
        tableSettingsSx,
        colWidths,
        updateColWidth,
        resetColWidths,
        scrollContainerRef,
        showRightShadow,
        toolbarRef,
        locationCompact,
        searchCompact,
        locationsData,
        selectedItemId,
        handleRowClick,
        openEditDrawer,
        handleRefresh,
        handleStatusFilterChange,
        handleImportPreviewTab,
        handlePurchaseOrdersTab,
        handleSelectImportJobId,
        handleRequestImport,
        handleImportFileSelected,
        handleImportUploadClose,
        handleImportMappingClose,
        handleImportMappingSubmit,
        buildFilteredExportInput,
        importTargetFields: INVENTORY_IMPORT_TARGET_FIELDS,
        listViewMode,
        selectedImportJobId,
        focusedPurchaseOrderId,
        hasPendingImportJob,
        importFile,
        isImportUploadOpen,
        isImportMappingOpen,
        optimisticImports,
        selectedOptimisticTempId,
        setSelectedOptimisticTempId,
        handleSelectOptimisticTempId,
        isImportHistoryOpen,
        onImportHistoryOpen: openImportHistory,
        onImportHistoryClose,
        isExportHistoryOpen,
        onExportHistoryOpen: openExportHistory,
        onExportHistoryClose,
        isOptionsOpen,
        onOptionsOpen,
        onOptionsClose,
        historyOpenedFromOptions,
        handleHistoryBackToOptions,
        isOptionsSwapInstant,
        highlightExportId,
        setHighlightExportId,
        committedModalJobId,
        setCommittedModalJobId,
        handleImportActivityClick,
        handleExportActivityClick,
        handleImportHistorySelectJob,
        handleLocationChange,
        handleFiltersChange,
        deleteTargetItem,
        handleDeleteRequest,
        handleDeleteModalClose,
        handleItemDeleted,
        handleViewExistingItem,
        isAddDrawerOpen,
        onAddDrawerOpen,
        onAddDrawerClose,
        isEditDrawerOpen,
        onEditDrawerOpen,
        onEditDrawerClose: handleEditDrawerClose,
        isDeleteModalOpen,
        isActivityLogOpen,
        onActivityLogOpen,
        onActivityLogClose,
        selectedItemIds,
        selectedStatuses,
        toggleItemSelected,
        toggleSelectAllOnPage,
        allPageSelected,
        isPageSelectionIndeterminate,
        clearSelection,
        handleRowStatusChange,
        handleBulkAction,
        isBulkActionLoading:
            bulkStatusMutation.isPending ||
            updateStatusMutation.isPending ||
            bulkDeleteMutation.isPending,
        isBulkDeleteOpen,
        onBulkDeleteClose,
        handleBulkDeleteConfirm,
        isBulkDeleteLoading: bulkDeleteMutation.isPending,
    };
}

export type InventoryListPageModel = ReturnType<typeof useInventoryListPage>;
